import { GAME_BY_ID } from '../shared/games';
import { archivePath, blogPath, gamePath, hasArchive } from '../shared/site';
import type { GameContent } from './types';
import './seo.css';

/**
 * The evergreen half of a game page: what the game is, how it works, how to
 * play it well, and the questions new players ask.
 *
 * Rendered by React below the live board, and rendered to static HTML at build
 * time by `scripts/prerender.mjs` from this same component — one source, so the
 * crawlable copy and the one players see can never disagree.
 *
 * Plain anchors rather than react-router `Link`s: the prerendered copy is
 * inert HTML with no router around it, and a full navigation is the correct
 * behaviour there anyway.
 */
export default function GameSeoContent({ content }: { content: GameContent }) {
  const game = GAME_BY_ID[content.id];
  return (
    <section className="seo" aria-label={`About ${game.title}`}>
      <nav className="seo-crumbs" aria-label="Breadcrumb">
        <a href="/">All puzzles</a>
        <span aria-hidden> › </span>
        <span>{game.title}</span>
      </nav>

      <h1 className="seo-h1">{content.h1}</h1>
      <p className="seo-intro">{content.intro}</p>

      <h2>How to play {game.title}</h2>
      <ol className="seo-steps">
        {content.howToPlay.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>

      {content.example && (
        <>
          <h2>{content.example.title}</h2>
          <p>{content.example.body}</p>
        </>
      )}

      <h2>{game.title} strategy</h2>
      {content.strategy.map((tip, i) => (
        <div key={i} className="seo-tip">
          <h3>{tip.heading}</h3>
          <p>{tip.body}</p>
        </div>
      ))}

      <h2>Frequently asked questions</h2>
      <dl className="seo-faq">
        {content.faq.map((item, i) => (
          <div key={i}>
            <dt>{item.q}</dt>
            <dd>{item.a}</dd>
          </div>
        ))}
      </dl>

      <h2>More puzzles</h2>
      <ul className="seo-links">
        {hasArchive(content.id) && (
          <li>
            <a href={archivePath(content.id)}>
              {game.title} archive — past puzzles, hints and answers
            </a>
          </li>
        )}
        {content.related.map((id) => {
          const other = GAME_BY_ID[id];
          if (!other) return null;
          return (
            <li key={id}>
              <a href={gamePath(id)}>
                {other.title} — {other.tagline.toLowerCase()}
              </a>
            </li>
          );
        })}
        <li>
          <a href={blogPath()}>Puzzle strategy guides</a>
        </li>
      </ul>
    </section>
  );
}
